import React, { useEffect, useMemo, useRef, useState } from 'react';
import './NewEOrder.css';
import { FaTimes, FaPlus, FaTrash, FaChevronDown, FaRegSave } from 'react-icons/fa';
const API = import.meta.env.VITE_API_URL;
// ---- client user helpers ----
function getLoggedInUser() {
  try {
    return JSON.parse(localStorage.getItem('currentUser') || localStorage.getItem('user') || '{}') || {};
  } catch { return {}; }
}
function getLoggedInClientId() {
  const u = getLoggedInUser();
  const cid = u.clientid ?? u.clientId ?? u.clientID ?? u.client ?? null;
  const n = Number(cid);
  return Number.isFinite(n) && n > 0 ? n : null;
}
function getLoggedInClientName() {
  const u = getLoggedInUser();
  return u.clientname || u.clientName || u.client_name || u.company || '';
}
function isClientUser() {
  const u = getLoggedInUser();
  const roleStr = [u.role, u.userrole, u.type, u.usertype, u.userType]
    .filter(Boolean).map(v => String(v).toLowerCase()).join(' ');
  const explicitClient = (u.isClient === true) || (u.isclient === true) || (u.client === true) || (u.clientuser === true);
  const explicitInternal = (u.isInternal === true) || (u.internal === true);
  const hasClientId = !!getLoggedInClientId();
  if (explicitInternal) return false;
  if (explicitClient) return true;
  if (hasClientId) return true;
  return roleStr.includes('client') && !roleStr.includes('internal');
}
/* ---------- constants ---------- */
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'PA', name: 'Panama' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IE', name: 'Ireland' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'CN', name: 'China' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IL', name: 'Israel' },
  { code: 'JP', name: 'Japan' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'SG', name: 'Singapore' },
  { code: 'KR', name: 'South Korea' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TR', name: 'Turkey' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DE', name: 'Germany' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GR', name: 'Greece' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NO', name: 'Norway' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'IT', name: 'Italy' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'ZA', name: 'South Africa' },
];
const emptyLine = () => ({ sku: '', qty: 1, description: '', imageUrl: '', unitValue: '' });

const sanitizeMoney = (val) => {
  if (val == null) return '';
  let s = String(val).replace(/[^0-9.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    const before = s.slice(0, firstDot + 1);
    const after = s.slice(firstDot + 1).replace(/\./g, '');
    s = before + after;
  }
  if (s.includes('.')) {
    const [intPart, decPartRaw] = s.split('.');
    const decPart = (decPartRaw || '').slice(0, 2);
    s = `${intPart}.${decPart}`;
  }
  if (s.startsWith('0') && s.length > 1 && s[1] !== '.') {
    s = s.replace(/^0+(?=\d)/, '');
  }
  return s;
};

const preventNonMoneyKeys = (e) => {
  if (e.ctrlKey || e.metaKey) return;
  const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape', 'Enter'];
  if (allowed.includes(e.key)) return;
  if (/^[0-9]$/.test(e.key)) return;
  if (e.key === '.') {
    if (e.currentTarget.value.includes('.')) e.preventDefault();
    return;
  }
  e.preventDefault();
};

export default function NewEOrder({ open, onClose, onCreated }) {
  const [suggestStyle, setSuggestStyle] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const IS_CLIENT = useMemo(() => isClientUser(), []);
  const CLIENT_ID = useMemo(() => getLoggedInClientId(), []);
  const CLIENT_NAME_FROM_USER = useMemo(() => getLoggedInClientName(), []);
  const [lockedClientName, setLockedClientName] = useState('');
  // validation state (invalid flags by field key)
  const [invalid, setInvalid] = useState({});
  // header form
  const [ordernumber, setOrdernumber] = useState('');
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [orderdate, setOrderdate] = useState(todayIso);
  // client dropdown
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  // ship-to (maps to eorders.shipping* fields)
  const [shipto, setShipto] = useState({
    company: '',
    firstname: '',
    lastname: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postal: '',
    country: 'US',
    phone: '',
    email: '',
  });
  // optional fields
  const [notes, setNotes] = useState('');
  const [shippingmethod, setShippingmethod] = useState('');
  const [shipbydate, setShipbydate] = useState('');
  const [shippingaccount, setShippingaccount] = useState('');

  // line items
  const [lines, setLines] = useState([emptyLine()]);
  // SKU suggestions
  const [suggestIdx, setSuggestIdx] = useState(-1);
  const [suggest, setSuggest] = useState([]);
  const listRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    // reset form each open
    setSaving(false); setError(''); setInvalid({});
    setOrdernumber('');
    setOrderdate(todayIso);
    setClientId('');
    setShipto({
      company: '', firstname: '', lastname: '',
      address1: '', address2: '', city: '', state: '', postal: '',
      country: 'US', phone: '', email: '',
    });
    setNotes(''); setShippingmethod(''); setShipbydate('');
    setShippingaccount('');
    setLines([emptyLine()]);
    setSuggestIdx(-1); setSuggest([]);
    // fetch clients (for client users: only associated/allowed clients)
    (async () => {
      try {
        const url = IS_CLIENT
          ? `${API}/api/reports/filters/clients`
          : `${API}/api/clients?limit=1000&activeOnly=1`;
        const res = await fetch(url);
        const j = await res.json();
        const data = Array.isArray(j?.data) ? j.data : (Array.isArray(j) ? j : []);
        const opts = data.map(c => ({ id: String(c.id), name: c.name })).filter(c => c.id && c.name);
        setClients(opts);
        // For client users, preselect user's client if present; else first option
        if (IS_CLIENT) {
          const found = opts.find(c => c.id === String(CLIENT_ID));
          setClientId(found ? found.id : (opts[0]?.id || ''));
          setLockedClientName(found ? found.name : (opts[0]?.name || ''));
        }
      } catch {
        setClients([]);
      }
    })();
  }, [open, todayIso]);
  if (!open) return null;
  /* ---------- helpers ---------- */
  const updateLine = (idx, patch) => setLines(prev => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (idx) => setLines(prev => prev.filter((_, i) => i !== idx));

  const fetchSkuSuggestions = async (idx, term) => {
    setSuggestIdx(-1);
    setSuggest([]);
    const effClientId = clientId || '';
    if (!effClientId || !term) { setSuggestStyle({}); return; }

    try {
      const res = await fetch(
        `${API}/api/items/lookupbysku?clientid=${encodeURIComponent(effClientId)}&sku=${encodeURIComponent(term)}`
      );
      const j = await res.json();
      const rows = Array.isArray(j) ? j : [];
      setSuggestIdx(idx);
      setSuggest(rows);

      // --- Compute full-width dropdown aligned to modal ---
      // Use the active element (the SKU input) + its nearest .neo-modal
      const inputEl = document.activeElement;
      const modalEl = inputEl?.closest?.('.neo-modal') || document.querySelector('.neo-modal');
      if (modalEl && inputEl) {
        const modalRect = modalEl.getBoundingClientRect();
        const inputRect = inputEl.getBoundingClientRect();
        // Small horizontal padding to match modal content; tune if needed
        const hPad = 18;
        setSuggestStyle({
          position: 'fixed',
          left: `${modalRect.left + hPad}px`,
          width: `${Math.max(300, modalRect.width - hPad * 2)}px`,
          top: `${inputRect.bottom + 4}px`,
          maxHeight: '40vh',
          overflowY: 'auto',
          background: 'var(--suggest-bg, #283141)',
          color: 'var(--suggest-fg, #fff)',
          boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
          borderRadius: 8,
          zIndex: 1000,
          whiteSpace: 'nowrap'
        });
      } else {
        // Fallback: keep default styling if we can't compute
        setSuggestStyle({});
      }
    } catch {
      setSuggestIdx(idx);
      setSuggest([]);
    }
  };
  const closeSuggestions = () => { setSuggestIdx(-1); setSuggest([]); + setSuggestStyle({}); };

  const onPickSuggestion = (idx, item) => {
    updateLine(idx, {
      sku: item?.sku || '',
      description: lines[idx]?.description || item?.description || '',
    });
    closeSuggestions();
  };
  const postJSON = async (url, body) => {
    const r = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j?.error || 'Request failed');
    return j;
  };
  // apply invalid style helper
  const inputClass = (base, key) => (invalid[key] ? `${base} is-invalid` : base);
  const markInvalid = (keys) => {
    const map = {};
    keys.forEach(k => { map[k] = true; });
    setInvalid(map);
  };
  const saveNewEOrder = async () => {
    setSaving(true);
    setError('');
    setInvalid({});

    try {
      // Required fields validation
      const reqKeys = [];
      if (!clientId) reqKeys.push('clientId');
      if (!ordernumber.trim()) reqKeys.push('ordernumber');
      if (!orderdate) reqKeys.push('orderdate');
      // Either company OR firstname must be filled
      if (!shipto.company.trim() && !shipto.firstname.trim()) {
        reqKeys.push('ship_company', 'ship_firstname');
      }
      // ship-to basics
      if (!shipto.address1.trim()) reqKeys.push('ship_address1');
      if (!shipto.city.trim()) reqKeys.push('ship_city');
      if (!shipto.postal.trim()) reqKeys.push('ship_postal');
      if (!shipto.country) reqKeys.push('ship_country');
      if (shipto.country === 'US' && !shipto.state) reqKeys.push('ship_state');

      if (reqKeys.length) {
        markInvalid(reqKeys);
        throw new Error('Please fill in all required fields, including either Company or First Name.');
      }

      // --- 1) Create header (manual order mapping) ---
      const header = {
        deleted: 0,
        datecreated: new Date().toISOString().slice(0, 19).replace('T', ' '),
        clientid: Number(clientId || CLIENT_ID || 0),
        contactemail: shipto.email || null,
        ordernumber: ordernumber.trim(),
        altordernumber: ordernumber.trim(),
        orderid: ordernumber.trim(),
        externalid: null,
        // Preserve time-of-day on creation by using current time
        orderdate: (() => {
          const now = new Date();
          const pad = (n) => String(n).padStart(2, '0');
          const hh = pad(now.getHours());
          const mm = pad(now.getMinutes());
          const ss = pad(now.getSeconds());
          return `${orderdate} ${hh}:${mm}:${ss}`;
        })(),
        subordercount: lines.length,
        servicetype: 'Manual',
        storeid: 0,
        servicename: '',
        our_order_status: 1,
        orderstatus: "unfulfilled",
        paymentstatus: "unpaid",
        itemstotal: 0,
        shippingtotal: 0,
        taxtotal: 0,
        total: 0,
        notes: notes || null,
        tags: null,
        billingcompany: null,
        billingfirstname: null,
        billinglastname: null,
        billingnickname: null,
        billingaddress1: null,
        billingaddress2: null,
        billingaddress3: null,
        billingcity: null,
        billingstate: null,
        billingcountry: null,
        billingpostalcode: null,
        billingphone: null,
        billingemail: null,
        shippingcompany: shipto.company || null,
        shippingfirstname: shipto.firstname || null,
        shippinglastname: shipto.lastname || null,
        shippingnickname: null,
        shippingaddress1: shipto.address1 || null,
        shippingaddress2: shipto.address2 || null,
        shippingaddress3: null,
        shippingcity: shipto.city || null,
        shippingstate: shipto.state || null,
        shippingcountry: shipto.country || 'US',
        shippingpostalcode: shipto.postal || null,
        shippingphone: shipto.phone || null,
        shippingemail: shipto.email || null,
        shippingmethod: shippingmethod || null,
        shippingaccount: shippingaccount || null,
        shipbydate: shipbydate || null,
        updatedat: null,
        externalcustomerid: null,
        externalcustomeremail: null,
      };
      const hJson = await postJSON(`${API}/api/eorders`, header);
      const eorderId = hJson?.id;
      if (!eorderId) throw new Error('Failed to create manual e-order');

      // --- 2) Add items (sanitize) ---
      const cleanLines = lines
        .map(l => ({
          sku: String(l.sku || '').trim(),
          qty: Number(l.qty || 0),
          description: (l.description || '').trim(),
          imageUrl: l.imageUrl ? String(l.imageUrl) : undefined,
          unitValue: (() => {
            const num = Number(l.unitValue);
            if (!Number.isFinite(num) || num < 0) return 0;
            return Math.round(num * 100) / 100;
          })(),
        }))
        .filter(l => l.sku && Number.isFinite(l.qty) && l.qty > 0);
      if (cleanLines.length) {
        await postJSON(`${API}/api/eorders/${eorderId}/items`, { lines: cleanLines });
      }

      onCreated?.(eorderId);
      onClose?.();
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="neo-overlay">
      <div className="neo-modal" onClick={(e) => e.stopPropagation()}>
        {/* Title bar */}
        <div className="neo-titlebar">
          <h3 className="neo-title">New E-Order (Manual)</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        {/* Scrollable body */}
        <div className="neo-body">
          <div className="neo-sections">
            <section className="neo-section">
              <div className="neo-section-header">
                <div className="neo-section-kicker">Order Setup</div>
                <div className="neo-section-title">Core Details</div>
              </div>
              <div className="neo-row">
                <label className="neo-field required">
                  <span>Client</span>
                  <div className="neo-select-wrap">
                    <select
                      className={inputClass('neo-select', 'clientId')}
                      value={clientId}
                      onChange={(e) => { setClientId(e.target.value); closeSuggestions(); setInvalid(v => ({ ...v, clientId: false })); }}
                    >
                      <option value="">— Select client —</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <FaChevronDown className="neo-caret" />
                  </div>
                </label>
                <label className="neo-field required">
                  <span>Order #</span>
                  <input
                    className={inputClass('neo-input', 'ordernumber')}
                    value={ordernumber}
                    onChange={(e) => { setOrdernumber(e.target.value); setInvalid(v => ({ ...v, ordernumber: false })); }}
                    placeholder="Enter a unique order number"
                  />
                </label>
                <label className="neo-field required">
                  <span>Order Date</span>
                  <input
                    type="date"
                    className={inputClass('neo-input', 'orderdate')}
                    value={orderdate}
                    onChange={(e) => { setOrderdate(e.target.value); setInvalid(v => ({ ...v, orderdate: false })); }}
                  />
                </label>
              </div>
            </section>

            <section className="neo-section">
              <div className="neo-section-header">
                <div className="neo-section-kicker">Delivery</div>
                <div className="neo-section-title">Ship To</div>
              </div>
            <div className="neo-grid">
              <label className="neo-field either-required">
                <span>Company <em style={{ fontStyle: 'normal', opacity: .7 }}>(one required)</em></span>
                <input
                  className={inputClass('neo-input', 'ship_company')}
                  value={shipto.company}
                  onChange={e => {
                    const v = e.target.value;
                    setShipto(s => ({ ...s, company: v }));
                    // Clear either/or invalid flags if either field now has a value
                    if (v.trim() || shipto.firstname.trim()) {
                      setInvalid(prev => ({ ...prev, ship_company: false, ship_firstname: false }));
                    }
                  }}
                />
              </label>
              <label className="neo-field either-required">
                <span>First name <em style={{ fontStyle: 'normal', opacity: .7 }}>(one required)</em></span>
                <input
                  className={inputClass('neo-input', 'ship_firstname')}
                  value={shipto.firstname}
                  onChange={e => {
                    const v = e.target.value;
                    setShipto(s => ({ ...s, firstname: v }));
                    if (v.trim() || shipto.company.trim()) {
                      setInvalid(prev => ({ ...prev, ship_firstname: false, ship_company: false }));
                    }
                  }}
                />
              </label>
              <label className="neo-field">
                <span>Last name</span>
                <input className="neo-input" value={shipto.lastname} onChange={e => setShipto(s => ({ ...s, lastname: e.target.value }))} />
              </label>
              <label className="neo-field neo-span-2 required">
                <span>Address 1</span>
                <input
                  className={inputClass('neo-input', 'ship_address1')}
                  value={shipto.address1}
                  onChange={e => { setShipto(s => ({ ...s, address1: e.target.value })); setInvalid(v => ({ ...v, ship_address1: false })); }}
                />
              </label>
              <label className="neo-field neo-span-2">
                <span>Address 2</span>
                <input className="neo-input" value={shipto.address2} onChange={e => setShipto(s => ({ ...s, address2: e.target.value }))} />
              </label>
              <label className="neo-field required">
                <span>City</span>
                <input
                  className={inputClass('neo-input', 'ship_city')}
                  value={shipto.city}
                  onChange={e => { setShipto(s => ({ ...s, city: e.target.value })); setInvalid(v => ({ ...v, ship_city: false })); }}
                />
              </label>
              <label className="neo-field required">
                <span>Country</span>
                <div className="neo-select-wrap">
                  <select
                    className={inputClass('neo-select', 'ship_country')}
                    value={shipto.country}
                    onChange={(e) => { setShipto(s => ({ ...s, country: e.target.value })); setInvalid(v => ({ ...v, ship_country: false })); }}
                  >
                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                  <FaChevronDown className="neo-caret" />
                </div>
              </label>
              <label className={`neo-field ${shipto.country === 'US' ? 'required' : ''}`}>
                <span>State/Prov</span>
                {shipto.country === 'US' ? (
                  <div className="neo-select-wrap">
                    <select
                      className={inputClass('neo-select', 'ship_state')}
                      value={shipto.state}
                      onChange={(e) => { setShipto(s => ({ ...s, state: e.target.value })); setInvalid(v => ({ ...v, ship_state: false })); }}
                    >
                      <option value="">— State —</option>
                      {US_STATES.map(st => <option key={st.code} value={st.code}>{st.name}</option>)}
                    </select>
                    <FaChevronDown className="neo-caret" />
                  </div>
                ) : (
                  <input className="neo-input" value={shipto.state} onChange={e => setShipto(s => ({ ...s, state: e.target.value }))} />
                )}
              </label>
              <label className="neo-field required">
                <span>Postal Code</span>
                <input
                  className={inputClass('neo-input', 'ship_postal')}
                  value={shipto.postal}
                  onChange={e => { setShipto(s => ({ ...s, postal: e.target.value })); setInvalid(v => ({ ...v, ship_postal: false })); }}
                />
              </label>
              <label className="neo-field">
                <span>Phone</span>
                <input className="neo-input" value={shipto.phone} onChange={e => setShipto(s => ({ ...s, phone: e.target.value }))} />
              </label>
              <label className="neo-field">
                <span>Email</span>
                <input className="neo-input" value={shipto.email} onChange={e => setShipto(s => ({ ...s, email: e.target.value }))} />
              </label>
            </div>
            <div className="neo-help" style={{ marginTop: 6 }}>
              * Required fields are marked. Company <strong>or</strong> First name is required.
            </div>
            </section>

            <section className="neo-section">
              <div className="neo-section-header">
                <div className="neo-section-kicker">Fulfillment</div>
                <div className="neo-section-title">Shipping & Notes</div>
              </div>
              <div className="neo-grid">
                <label className="neo-field">
                  <span>Ship By</span>
                  <input className="neo-input" type="date" value={shipbydate} onChange={e => setShipbydate(e.target.value)} />
                </label>
                <label className="neo-field">
                  <span>Shipping Method</span>
                  <input
                    className="neo-input"
                    value={shippingmethod}
                    onChange={e => setShippingmethod(e.target.value)}
                  />
                </label>
                <label className="neo-field">
                  <span>Shipping Account #</span>
                  <input
                    className="neo-input"
                    value={shippingaccount}
                    onChange={e => setShippingaccount(e.target.value)}
                    placeholder="e.g., UPS/FedEx account"
                  />
                </label>
                <label className="neo-field neo-span-2">
                  <span>Notes</span>
                  <textarea className="neo-textarea" rows={3} value={notes} onChange={e => setNotes(e.target.value)} />
                </label>
              </div>
            </section>

            <section className="neo-section neo-section--accent">
              <div className="neo-section-header">
                <div className="neo-section-kicker">Order Contents</div>
                <div className="neo-section-title">Line Items</div>
              </div>
              <div className="neo-lines">
              <div className="neo-lines-head">
                <div>SKU</div>
                <div>Qty</div>
                <div>Description</div>
                <div>Unit Value (USD)</div>
                <div className="t-right">Actions</div>
              </div>
              {lines.map((row, idx) => (
                <div className="neo-line" key={idx}>
                  {/* SKU with suggestions */}
                  <div className="sku-cell">
                    <div className="sku-wrap">
                      <input
                        className="neo-input sku-input"
                        placeholder="Start typing a SKU…"
                        value={row.sku}
                        onChange={(e) => {
                          updateLine(idx, { sku: e.target.value });
                          fetchSkuSuggestions(idx, e.target.value);
                        }}
                        onFocus={(e) => fetchSkuSuggestions(idx, e.target.value)}
                        onBlur={() => {
                          setTimeout(() => {
                            if (!listRef.current?.contains(document.activeElement)) {
                              closeSuggestions();
                            }
                          }, 150);
                        }}
                      />
                      {suggestIdx === idx && suggest.length > 0 && (
                        <div
                          ref={listRef}
                          className="sku-suggest"
                          style={suggestStyle}  // <-- full-width, fixed dropdown when computed
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {suggest.map(s => (
                            <button
                              type="button"
                              key={s.itemid || s.sku}
                              className="sku-suggest-row"
                              onClick={() => onPickSuggestion(idx, s)}
                              style={{ display: 'flex', justifyContent: 'space-between', gap: 12, width: '100%' }}
                            >
                              <div className="s1" style={{ fontWeight: 700 }}>{s.sku}</div>
                              <div className="s2" style={{ opacity: .85, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {s.description}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Qty */}
                  <div>
                    <input
                      type="number"
                      className="neo-input qty-input"
                      min={1}
                      value={row.qty}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        updateLine(idx, { qty: Number.isFinite(v) && v > 0 ? v : 1 });
                      }}
                    />
                  </div>
                  {/* Description */}
                  <div>
                    <input
                      className="neo-input desc-input"
                      placeholder="Optional"
                      value={row.description}
                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                    />
                  </div>
                  {/* Unit Value */}
                  <div>
                    <input
                      className="neo-input value-input"
                      placeholder="0.00"
                      inputMode="decimal"
                      value={row.unitValue}
                      onKeyDown={preventNonMoneyKeys}
                      onChange={(e) => updateLine(idx, { unitValue: sanitizeMoney(e.target.value) })}
                    />
                  </div>
                  {/* Actions */}
                  <div className="t-right">
                    <button className="mini-btn danger" type="button" onClick={() => removeLine(idx)}>
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
              <div>
                <button className="btn" type="button" onClick={addLine}>
                  <FaPlus /> Add Line
                </button>
              </div>
              </div>
            </section>
          </div>
          {error && <div className="neo-error">{error}</div>}
        </div>
        {/* Footer actions */}
        <div className="neo-actions">
          <button className="btn" onClick={onClose}>
            <FaTimes /> Close
          </button>
          <button className="btn primary" onClick={saveNewEOrder} disabled={saving}>
            <FaRegSave /> {saving ? 'Saving…' : 'Create E-Order'}
          </button>
        </div>
      </div>
    </div>
  );
}
